import { Link } from "react-router-dom";

/**
 * Official SRES brand mark — Shanvi Resource Estimation Studio.
 *
 * Modes:
 *   • default (icon only) — the hexagonal blue/black "S" mark
 *   • withText="inline"   — mark + horizontal SRES wordmark (header use)
 *   • withText="stacked"  — mark + stacked wordmark + tagline (login/hero use)
 *
 * All variants pull from `/sres-mark.png` (transparent PNG rendered from the
 * approved HD source). Wordmark typography sticks to the studio's display
 * face (`font-heading`) with tightened tracking so the "S R E S" block reads
 * as a single logotype rather than four letters.
 */
export default function SresLogo({
  size = 40,
  className = "",
  withText = false,      // false | "inline" | "stacked" | true (legacy = stacked)
  linkTagline = true,
}) {
  const mode = withText === true ? "stacked" : withText || "mark";

  const mark = (
    <img
      src="/sres-mark.png"
      alt="SRES"
      className={`shrink-0 select-none ${className}`}
      draggable={false}
      style={{ height: size, width: "auto" }}
    />
  );

  if (mode === "mark") return mark;

  if (mode === "inline") {
    // Compact horizontal lockup — used in the top bar.
    return (
      <div className={`flex items-center gap-2.5 ${className}`}>
        {mark}
        <div className="flex flex-col leading-none">
          <div className="font-heading font-black tracking-[0.24em] text-[15px]">
            <span className="text-foreground">SRE</span><span className="text-brand-blue">S</span>
          </div>
          <div className="text-[8.5px] uppercase tracking-[0.28em] text-muted-foreground mt-1">
            Shanvi Resource Estimation Studio
          </div>
        </div>
      </div>
    );
  }

  // stacked (default withText)
  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {mark}
      <div className="flex flex-col leading-none">
        <div className="font-heading font-black tracking-[0.22em] text-2xl leading-none">
          <span className="text-foreground">SRE</span><span className="text-brand-blue">S</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1.5">
          Shanvi Resource Estimation Studio
        </div>
        {linkTagline && (
          <Link
            to="/about"
            className="text-[9px] uppercase tracking-[0.28em] text-brand-blue/80 hover:text-brand-blue mt-1 inline-block transition-colors"
            data-testid="brand-product-of-link"
          >
            A Product of Shanvi Resources →
          </Link>
        )}
      </div>
    </div>
  );
}
